import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { anrufkampagnenAPI, authAPI, schnellerfassungAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  PhoneCall, Plus, Loader2, RefreshCw, ChevronRight, ArrowLeft, Phone, Mail, CalendarClock,
  UserCheck, Handshake, ThumbsDown, PhoneOff, Ban, PhoneMissed, Info, StopCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Markdown from '@/components/Markdown';
import { useAuth } from '@/contexts/AuthContext';
const STAND_KURZ = { mitglied: 'Mitglied', ehemaliges_mitglied: 'ehemaliges Mitglied', da_gewesen: 'war da', lead: null };

/* Lead-Anrufliste (Bauauftrag 24.09.2026, Teil B)
 *
 * /kampagnen/anrufe        Kampagnen mit Fortschritt (Anrufe / Ziel), neue Kampagne
 * /kampagnen/anrufe/:id    Übersicht + offene Leads nach nächstem fälligen Versuch,
 *                          Gesprächsmaske nach dem Muster der Reaktivierung:
 *                          links Lead, Mitte Skript (Markdown-Datei), rechts Ergebnis.
 * Regeln (Server): 3 Versuche mit 2 Tagen Abstand, Sperre bei "nicht mehr kontaktieren".
 */

const ERGEBNIS = [
  { key: 'nicht_erreicht', label: 'Nicht erreicht', icon: PhoneMissed, cls: 'border-border' },
  { key: 'rueckruf_vereinbart', label: 'Rückruf vereinbart', icon: CalendarClock, cls: 'border-amber-300 text-amber-700' },
  { key: 'probetraining_gebucht', label: 'Probetraining gebucht', icon: Handshake, cls: 'border-emerald-300 text-emerald-700' },
  { key: 'kein_interesse', label: 'Kein Interesse', icon: ThumbsDown, cls: 'border-border text-muted-foreground' },
  { key: 'falsche_nummer', label: 'Falsche Nummer', icon: PhoneOff, cls: 'border-border text-muted-foreground' },
  { key: 'nicht_mehr_kontaktieren', label: 'Nicht mehr kontaktieren', icon: Ban, cls: 'border-red-300 text-red-600' },
];
const EINWILLIGUNG_LABEL = {
  marketing_consent: 'Marketing-Einwilligung', telefon_einwilligung: 'Telefon-Einwilligung',
  fest_kontakt_erlaubt: 'Kontakt erlaubt (Fest)', probetraining_angefragt: 'hat Probetraining angefragt',
  selbst_angefragt: 'hat selbst angefragt', von_hand: 'von Hand ausgewählt, keine dokumentierte Einwilligung',
};

/* ─── Leads von Hand auswählen (07.10.2026) ────────────────────────────────
 * Suche über alle Leads (auch Altbestand, Archiv, Mitglieder), Haken setzen,
 * dazu Leads von Hand eintragen, die es im CRM noch nicht gibt (Altbestand). */
const B = 'text-[10px] px-1.5 py-0.5 rounded-full';
/* Kennzeichen eines Leads in der Auswahl und in der Kampagnenliste (Modulebene, sonst baut React die
   Badges bei jedem Tastendruck neu auf) */
function Kennzeichen({ l, kampagneName }) {
  return (
    <span className="flex flex-wrap gap-1">
      {l.in_kampagne && <span className={`${B} bg-amber-100 text-amber-700`}>{l.in_kampagne === kampagneName ? 'schon in dieser Liste' : `schon in „${l.in_kampagne}“`}</span>}
      {l.mitglied && <span className={`${B} bg-emerald-100 text-emerald-700`}>Mitglied mit Vertrag</span>}
      {l.altbestand && <span className={`${B} bg-muted text-muted-foreground`}>Altbestand</span>}
      {l.archiviert && <span className={`${B} bg-muted text-muted-foreground`}>archiviert</span>}
      {l.gesperrt && <span className={`${B} bg-red-100 text-red-700`}>nicht mehr kontaktieren</span>}
      {l.ohne_telefon && <span className={`${B} bg-red-100 text-red-700`}>keine Nummer</span>}
      {l.telefon_ungueltig && <span className={`${B} bg-red-100 text-red-700`}>Nummer als falsch gemeldet</span>}
      {!l.gesperrt && !l.ohne_telefon && !l.telefon_ungueltig && (l.einwilligung == null || l.einwilligung === 'von_hand') && <span className={`${B} bg-amber-100 text-amber-700`}>ohne Einwilligung</span>}
    </span>
  );
}
const nichtWaehlbar = (l) => !!(l.gesperrt || l.ohne_telefon || l.telefon_ungueltig);
const nummerKern = (t) => String(t || '').replace(/\D/g, '').slice(-8);

/* CSV lesen (Octiv-Export: "ID","Name","Email","Mobile",… — oder jede Datei mit Spalten name/telefon/email).
   Anführungszeichen, Komma oder Semikolon, eine Zeile je Person. */
function csvLesen(text) {
  const zeilen = [];
  let feld = '', zeile = [], inAnf = false;
  const t = text.replace(/^﻿/, '');
  const trenner = (t.split('\n')[0].match(/;/g) || []).length > (t.split('\n')[0].match(/,/g) || []).length ? ';' : ',';
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (inAnf) {
      if (c === '"') { if (t[i + 1] === '"') { feld += '"'; i++; } else inAnf = false; }
      else feld += c;
    } else if (c === '"') inAnf = true;
    else if (c === trenner) { zeile.push(feld); feld = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && t[i + 1] === '\n') i++; zeile.push(feld); zeilen.push(zeile); zeile = []; feld = ''; }
    else feld += c;
  }
  if (feld.length || zeile.length) { zeile.push(feld); zeilen.push(zeile); }
  if (!zeilen.length) return [];
  const kopf = zeilen[0].map(k => k.trim().toLowerCase());
  const spalte = (...namen) => kopf.findIndex(k => namen.includes(k));
  const iName = spalte('name', 'vorname nachname'), iTel = spalte('mobile', 'telefon', 'phone', 'handy', 'nummer', 'mobil'),
    iMail = spalte('email', 'e-mail', 'mail'), iId = spalte('id', 'octiv_id'), iVor = spalte('vorname', 'first name'), iNach = spalte('nachname', 'last name'),
    iVon = spalte('from', 'quelle', 'source'), iErfasst = spalte('captured', 'erfasst von'), iFolge = spalte('next follow up', 'wiedervorlage');
  if (iName < 0 && iVor < 0) return null;
  // Octiv speichert Handynummern teils ohne führende 0 („1638053485") — die gehört davor
  const nummer = (roh) => { const n = (roh || '').replace(/[^\d+]/g, ''); return /^1[5-7]\d{8,9}$/.test(n) ? `0${n}` : n; };
  const wert = (z, i) => (i >= 0 ? (z[i] || '') : '').trim();
  return zeilen.slice(1).filter(z => z.some(x => (x || '').trim())).map(z => {
    const extras = [wert(z, iVon) && `Quelle ${wert(z, iVon)}`, wert(z, iErfasst) && `erfasst von ${wert(z, iErfasst)}`, wert(z, iFolge) && `Wiedervorlage ${wert(z, iFolge)}`].filter(Boolean);
    return {
      name: (iName >= 0 ? wert(z, iName) : `${wert(z, iVor)} ${wert(z, iNach)}`).replace(/\s+/g, ' ').trim(),
      telefon: nummer(wert(z, iTel)),
      email: wert(z, iMail),
      extern_id: wert(z, iId),
      zusatz: extras.join(', '),
    };
  });
}
const STAND_FARBE = { mitglied: 'bg-emerald-100 text-emerald-700', ehemaliges_mitglied: 'bg-sky-100 text-sky-700', da_gewesen: 'bg-violet-100 text-violet-700', lead: 'bg-muted text-muted-foreground', mehrdeutig: 'bg-red-100 text-red-700', fehlt: 'bg-amber-100 text-amber-700' };
const STAND_ALLE = ['mitglied', 'ehemaliges_mitglied', 'da_gewesen', 'lead', 'mehrdeutig', 'fehlt'];
/* Zeile aus dem Abgleich: anhakbar? (gesperrt, falsche Nummer, keine Nummer, mehrdeutig, schon in dieser Kampagne) */
const abgleichGesperrt = (z, kampagneName) => !!(z.gesperrt || z.telefon_ungueltig || z.ohne_telefon || (z.mehrdeutig || []).length || (kampagneName && z.in_kampagne === kampagneName));

function LeadAuswahl({ gewaehlt, setGewaehlt, neue, setNeue, kampagneName, setTelefonJeLead }) {
  const [q, setQ] = useState('');
  const [nur, setNur] = useState('alle');
  const [treffer, setTreffer] = useState([]);
  const [limit, setLimit] = useState(100);
  const [sucht, setSucht] = useState(false);
  const [hand, setHand] = useState({ name: '', telefon: '' });
  useEffect(() => {
    if (!q.trim() && nur === 'alle') { setTreffer([]); setSucht(false); return undefined; }
    let aktiv = true;
    setSucht(true);
    const h = setTimeout(() => {
      anrufkampagnenAPI.leadsSuchen(q.trim(), nur).then(r => { if (aktiv) { setTreffer(r.data.leads || []); setLimit(r.data.limit || 100); } })
        .catch(() => { if (aktiv) setTreffer([]); }).finally(() => { if (aktiv) setSucht(false); });
    }, 250);
    return () => { aktiv = false; clearTimeout(h); };
  }, [q, nur]);
  const istGewaehlt = (id) => gewaehlt.some(g => g.lead_id === id);
  const umschalten = (l) => {
    if (nichtWaehlbar(l) || l.in_kampagne === kampagneName) return;
    setGewaehlt(p => istGewaehlt(l.lead_id) ? p.filter(g => g.lead_id !== l.lead_id) : [...p, l]);
  };
  const [liste, setListe] = useState('');
  const [listeOffen, setListeOffen] = useState(false);
  /* Octiv-Liste (CSV) abgleichen (07.10.2026): je Zeile Mitglied / ehemalig / da gewesen / Lead / fehlt,
     dann die Haken übernehmen — bekannte Leads werden angehakt, fehlende als neue Leads eingetragen */
  const [abgleich, setAbgleich] = useState(null);
  const [abgleichHaken, setAbgleichHaken] = useState({});
  const [gleicht, setGleicht] = useState(false);
  const [csvZeilen, setCsvZeilen] = useState({});     // Index -> Originalzeile (Zusatz aus der Datei für die Notiz)
  const dateiLesen = async (datei) => {
    if (!datei) return;
    setGleicht(true);
    try {
      const text = await datei.text();
      const zeilen = csvLesen(text);
      if (zeilen === null) { toast.error('Keine Spalte „Name“ oder „Vorname“ gefunden'); return; }
      if (!zeilen.length) { toast.error('Die Datei enthält keine Zeilen'); return; }
      const r = (await anrufkampagnenAPI.abgleichListe(zeilen.map(({ zusatz, ...z }) => z))).data;
      setAbgleich(r);
      setCsvZeilen(Object.fromEntries(zeilen.map((z, i) => [i, z])));
      // Vorbelegung: alle, die sich anrufen lassen — ohne Mitglieder mit Vertrag, Elternkonten, Gesperrte,
      // falsche/fehlende Nummer, mehrdeutige und schon in dieser Kampagne
      const h = {};
      r.zeilen.forEach((z, i) => { h[i] = !(z.mitglied || z.eltern || abgleichGesperrt(z, kampagneName)); });
      setAbgleichHaken(h);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Datei konnte nicht gelesen oder abgeglichen werden'); }
    finally { setGleicht(false); }
  };
  /* Was die Haken tatsächlich ergeben (ohne Doppelte gegen Auswahl und Liste) — für Knopf und Übernahme dieselbe Rechnung */
  const abgleichBerechnen = () => {
    const dazuLeads = [], dazuNeue = [], nachtragen = {};
    let doppelt = 0;
    (abgleich?.zeilen || []).forEach((z, i) => {
      if (!abgleichHaken[i]) return;
      if (z.lead_id) {
        if (gewaehlt.some(g => g.lead_id === z.lead_id) || dazuLeads.some(g => g.lead_id === z.lead_id)) { doppelt++; return; }
        dazuLeads.push({ lead_id: z.lead_id, name: z.lead_name || z.name, telefon: z.telefon_crm || z.telefon, einwilligung: z.einwilligung, gesperrt: z.gesperrt, ohne_telefon: z.ohne_telefon, telefon_ungueltig: z.telefon_ungueltig, mitglied: z.mitglied, altbestand: z.altbestand, archiviert: z.archiviert, in_kampagne: z.in_kampagne, stand: z.stand });
        if (z.telefon_nachtragen) nachtragen[z.lead_id] = z.telefon_nachtragen;
      } else if (schonDrin(z.telefon, dazuNeue)) { doppelt++; }
      else {
        const zusatz = csvZeilen[i]?.zusatz;
        dazuNeue.push({ name: z.name, telefon: z.telefon, email: z.email || undefined, notiz: [z.extern_id ? `Octiv-ID ${z.extern_id}` : '', zusatz].filter(Boolean).join(', ') || undefined });
      }
    });
    return { dazuLeads, dazuNeue, nachtragen, doppelt };
  };
  const abgleichUebernehmen = () => {
    if (!abgleich) return;
    const { dazuLeads, dazuNeue, nachtragen, doppelt } = abgleichBerechnen();
    if (dazuLeads.length) setGewaehlt(p => [...p, ...dazuLeads]);
    if (dazuNeue.length) setNeue(p => [...p, ...dazuNeue]);
    if (Object.keys(nachtragen).length && setTelefonJeLead) setTelefonJeLead(p => ({ ...(p || {}), ...nachtragen }));
    toast.success(`${dazuLeads.length} bekannte Leads angehakt, ${dazuNeue.length} neue aus der Liste eingetragen${doppelt ? `, ${doppelt} waren schon in der Auswahl` : ''}${Object.keys(nachtragen).length ? `, ${Object.keys(nachtragen).length} Nummern werden am Lead ergänzt` : ''}`, { duration: 8000 });
    setAbgleich(null); setAbgleichHaken({}); setCsvZeilen({});
  };
  const schonDrin = (telefon, zusatz = []) => {
    const kern = nummerKern(telefon);
    return neue.some(n => nummerKern(n.telefon) === kern) || gewaehlt.some(l => nummerKern(l.telefon) === kern) || zusatz.some(n => nummerKern(n.telefon) === kern);
  };
  const handEintragen = () => {
    const name = hand.name.trim(), telefon = hand.telefon.trim();
    if (name.length < 2 || telefon.replace(/\D/g, '').length < 8) { toast.error('Bitte Name und eine vollständige Telefonnummer eintragen'); return; }
    if (schonDrin(telefon)) { toast.info('Diese Nummer ist schon in der Liste'); return; }
    setNeue(p => [...p, { name, telefon }]);
    setHand({ name: '', telefon: '' });
  };
  /* Papierliste (07.10.2026): eine Zeile je Person, „Name, Nummer“ / „Name; Nummer“ / „Name  Nummer“ —
     die Nummer ist der Teil mit den Ziffern, der Rest ist der Name */
  const listeUebernehmen = () => {
    const dazu = [], kaputt = [];
    liste.split(/\r?\n/).map(z => z.trim()).filter(Boolean).forEach(zeile => {
      const m = zeile.match(/(\+?[\d][\d\s/().-]{6,}\d)/);
      const telefon = m ? m[1].trim() : '';
      const putz = (t) => t.replace(/[;,\t|]+/g, ' ').replace(/\s+/g, ' ').trim();
      // Name ist, was vor der Nummer steht; steht davor nichts, dann das dahinter (Notizen hinter der Nummer bleiben weg)
      const name = m ? (putz(zeile.slice(0, m.index)) || putz(zeile.slice(m.index + m[1].length))) : putz(zeile);
      if (!telefon || telefon.replace(/\D/g, '').length < 8 || name.length < 2) { kaputt.push(zeile); return; }
      if (schonDrin(telefon, dazu)) return;
      dazu.push({ name, telefon });
    });
    if (dazu.length) setNeue(p => [...p, ...dazu]);
    setListe(kaputt.join('\n'));
    if (kaputt.length) toast.warning(`${kaputt.length} Zeilen ohne Name oder vollständige Nummer bleiben im Feld stehen`, { duration: 8000 });
    if (dazu.length) toast.success(`${dazu.length} aus der Liste übernommen`);
    else if (!kaputt.length) toast.info('Nichts Neues in der Liste');
  };
  return (
    <div className="space-y-3" data-testid="ak-hand">
      <div className="flex gap-2">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Name, Telefon oder E-Mail suchen …" aria-label="Leads suchen" data-testid="ak-hand-suche" />
        <select value={nur} onChange={e => setNur(e.target.value)} aria-label="Gruppe" className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="ak-hand-nur">
          <option value="alle">Alle Leads</option><option value="altbestand">Altbestand & Archiv</option><option value="mitglieder">Mitglieder</option>
        </select>
      </div>
      <div className="rounded-lg border border-border/60 max-h-56 overflow-y-auto divide-y divide-border/50">
        {sucht && treffer.length === 0 ? <div className="p-3 text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Suche …</div>
          : treffer.length === 0 ? <div className="p-3 text-xs text-muted-foreground">{q.trim() || nur !== 'alle' ? 'Nichts gefunden.' : 'Suchwort eingeben oder eine Gruppe wählen.'}</div>
          : treffer.map(l => {
            const gesperrtHier = nichtWaehlbar(l) || l.in_kampagne === kampagneName;
            return (
              <label key={l.lead_id} className={`flex items-start gap-2 px-3 py-2 text-xs ${gesperrtHier ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/40'}`} data-testid={`ak-hand-treffer-${l.lead_id}`}>
                <input type="checkbox" className="mt-0.5 accent-marke" checked={istGewaehlt(l.lead_id)} disabled={gesperrtHier} onChange={() => umschalten(l)} />
                <span className="flex-1 min-w-0">
                  <span className="font-medium">{l.name}</span> <span className="font-mono text-muted-foreground">{l.telefon || '—'}</span>
                  <span className="block text-muted-foreground">{l.herkunft} · {l.zielgruppe === 'kinder' ? 'Kind' : 'Erw.'} · Stufe {l.stage || '—'} · letzter Kontakt {datum(l.letzter_kontakt)}</span>
                  <Kennzeichen l={l} kampagneName={kampagneName} />
                </span>
              </label>
            );
          })}
        {treffer.length >= limit && <div className="p-2 text-[11px] text-amber-700">Nur die ersten {limit} Treffer (nach Name). Bitte Suchwort eingrenzen.</div>}
      </div>
      <div className="rounded-lg border border-dashed border-border/60 p-3 space-y-2">
        <div className="text-xs font-bold">Lead von Hand eintragen (Altbestand, noch nicht im CRM)</div>
        <div className="flex flex-wrap gap-2">
          <Input value={hand.name} onChange={e => setHand(p => ({ ...p, name: e.target.value }))} placeholder="Name" aria-label="Name des neuen Leads" className="flex-1 min-w-[140px]" data-testid="ak-hand-name" />
          <Input value={hand.telefon} onChange={e => setHand(p => ({ ...p, telefon: e.target.value }))} placeholder="Telefon" aria-label="Telefon des neuen Leads" className="flex-1 min-w-[140px]" data-testid="ak-hand-telefon" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handEintragen(); } }} />
          <Button type="button" variant="outline" size="sm" onClick={handEintragen} data-testid="ak-hand-eintragen"><Plus className="w-3.5 h-3.5 mr-1" /> Dazu</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Gibt es die Nummer schon im CRM, wird der vorhandene Lead genommen. Sonst entsteht ein Lead als Altbestand, der nicht in die Vertriebsauswertung zählt.</p>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="text-xs text-primary hover:underline" onClick={() => setListeOffen(o => !o)} data-testid="ak-hand-liste-knopf">{listeOffen ? 'Ganze Liste ausblenden' : 'Ganze Liste auf einmal eintragen (Papierliste)'}</button>
          <label className={`text-xs text-primary hover:underline ${gleicht ? 'pointer-events-none opacity-60' : 'cursor-pointer'}`}>
            {gleicht ? 'Gleicht ab …' : 'Octiv-Liste (CSV) abgleichen'}
            <input type="file" accept=".csv,text/csv" className="hidden" disabled={gleicht} onChange={e => { dateiLesen(e.target.files?.[0]); e.target.value = ''; }} data-testid="ak-hand-csv" />
          </label>
        </div>
        {abgleich && (() => { const rechnung = abgleichBerechnen(); return (
          <div className="rounded-lg border border-border/60 bg-background p-2 space-y-2" data-testid="ak-hand-abgleich">
            <div className="text-xs flex flex-wrap gap-2 items-center">
              <b>{abgleich.summen.zeilen} Zeilen:</b>
              {STAND_ALLE.filter(s => abgleich.summen[s] > 0 || s !== 'mehrdeutig').map(s => <span key={s} className={`${B} ${STAND_FARBE[s]}`}>{abgleich.summen[s]} {abgleich.label[s]}</span>)}
              {abgleich.summen.gesperrt > 0 && <span className={`${B} bg-red-100 text-red-700`}>{abgleich.summen.gesperrt} gesperrt</span>}
              {abgleich.summen.ohne_nummer > 0 && <span className={`${B} bg-red-100 text-red-700`}>{abgleich.summen.ohne_nummer} ohne Nummer</span>}
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-border/50 text-xs">
              {abgleich.zeilen.map((z, i) => {
                const gesperrtHier = abgleichGesperrt(z, kampagneName);
                const nummerAnzeige = z.lead_id ? (z.telefon_nachtragen || z.telefon_crm || '—') : (z.telefon || '—');
                return (
                  <label key={i} className={`flex items-start gap-2 px-1 py-1.5 ${gesperrtHier ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/40'}`}>
                    <input type="checkbox" className="mt-0.5 accent-marke" checked={!!abgleichHaken[i]} disabled={gesperrtHier} onChange={() => setAbgleichHaken(h => ({ ...h, [i]: !h[i] }))} />
                    <span className="flex-1 min-w-0">
                      <span className="font-medium">{z.name}</span> <span className="font-mono text-muted-foreground">{nummerAnzeige}</span>
                      {z.lead_name && z.lead_name !== z.name && <span className="text-muted-foreground"> · im CRM als {z.lead_name}</span>}
                      <span className="block">
                        <span className={`${B} ${STAND_FARBE[z.stand]}`}>{abgleich.label[z.stand]}</span>
                        {z.lead_id && <span className="text-muted-foreground"> · {z.checkins || 0} Check-ins · {z.probetrainings_erschienen || 0}/{z.probetrainings || 0} Probetrainings{z.usc_besuche ? ` · ${z.usc_besuche} USC-Besuche` : ''} · letzter Kontakt {datum(z.letzter_kontakt)}{z.archiviert ? ' · archiviert' : ''}</span>}
                        {z.eltern && <span className="text-amber-700"> · Elternkonto (Vertrag und Besuche hängen am Kind)</span>}
                        {z.telefon_nachtragen && <span className="text-amber-700"> · im CRM ohne brauchbare Nummer, die Listen-Nummer wird am Lead ergänzt</span>}
                        {z.nummer_weicht_ab && <span className="text-amber-700"> · Liste {z.telefon}, CRM {z.telefon_crm}: angerufen wird die CRM-Nummer</span>}
                        {z.mehrdeutig.length > 0 && <span className="text-red-700"> · Nummer gehört mehreren: {z.mehrdeutig.join(', ')}</span>}
                        {z.gesperrt && <span className="text-red-700"> · nicht mehr kontaktieren</span>}
                        {z.telefon_ungueltig && !z.telefon_nachtragen && <span className="text-red-700"> · Nummer als falsch gemeldet</span>}
                        {z.ohne_telefon && <span className="text-red-700"> · keine Nummer</span>}
                        {z.in_kampagne && <span className="text-amber-700"> · {z.in_kampagne === kampagneName ? 'schon in dieser Liste' : `schon in „${z.in_kampagne}“`}</span>}
                        {!z.einwilligung && !z.gesperrt && z.stand !== 'mehrdeutig' && <span className="text-amber-700"> · ohne Einwilligung</span>}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">Vorbelegt sind alle, die sich anrufen lassen. Mitglieder mit Vertrag und Elternkonten sind ausgehakt, Haken setzt du selbst.</span>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => { setAbgleich(null); setAbgleichHaken({}); setCsvZeilen({}); }}>Verwerfen</Button>
                <Button type="button" variant="outline" size="sm" onClick={abgleichUebernehmen} disabled={rechnung.dazuLeads.length + rechnung.dazuNeue.length === 0} data-testid="ak-hand-abgleich-uebernehmen"><Plus className="w-3.5 h-3.5 mr-1" /> {rechnung.dazuLeads.length + rechnung.dazuNeue.length} übernehmen{rechnung.doppelt ? ` (${rechnung.doppelt} schon drin)` : ''}</Button>
              </div>
            </div>
          </div>
        ); })()}
        {listeOffen && (
          <div className="space-y-2" data-testid="ak-hand-liste">
            <textarea value={liste} onChange={e => setListe(e.target.value)} rows={6} aria-label="Liste, eine Zeile je Person" placeholder={'Eine Zeile je Person, Name und Nummer:\nAnna Beispiel, 0176 1234567\nBernd Muster; 07231 987654'} className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono" />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">Zeilen ohne Name oder vollständige Nummer bleiben stehen, damit du sie nachbessern kannst.</span>
              <Button type="button" variant="outline" size="sm" onClick={listeUebernehmen} disabled={!liste.trim()} data-testid="ak-hand-liste-uebernehmen"><Plus className="w-3.5 h-3.5 mr-1" /> Zeilen übernehmen</Button>
            </div>
          </div>
        )}
      </div>
      {(gewaehlt.length > 0 || neue.length > 0) && (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs space-y-1" data-testid="ak-hand-auswahl">
          <div className="font-bold">{gewaehlt.length + neue.length} in der Liste</div>
          <div className="flex flex-wrap gap-1">
            {gewaehlt.map(l => <button key={l.lead_id} type="button" onClick={() => setGewaehlt(p => p.filter(g => g.lead_id !== l.lead_id))} className="px-2 py-0.5 rounded-full border border-border/60 hover:bg-muted" title="Entfernen">{l.name} ×</button>)}
            {neue.map((n, i) => <button key={`${n.telefon}-${i}`} type="button" onClick={() => setNeue(p => p.filter((_, j) => j !== i))} className="px-2 py-0.5 rounded-full border border-dashed border-border/60 hover:bg-muted" title="Entfernen">{n.name} ({n.telefon}) ×</button>)}
          </div>
          {(gewaehlt.some(l => !l.einwilligung) || neue.length > 0) && <div className="flex items-start gap-1 text-amber-700"><Info className="w-3.5 h-3.5 mt-0.5 flex-none" /> Ohne dokumentierte Telefon-Einwilligung darf nicht angerufen werden (UWG § 7). Wer die Liste zusammenstellt, trägt das; es wird je Lead vermerkt.</div>}
        </div>
      )}
    </div>
  );
}

function datumZeit(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
}
function datum(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return iso; }
}

/* ─── Neue Kampagne ─────────────────────────────────────────────────────── */
function NeueKampagne({ offen, onClose, onFertig, werte }) {
  const [f, setF] = useState({ name: '', ziel: 50, tage_seit_kontakt: 30, einwilligung: 'streng', zielgruppe: '', herkunft: '', erstkontakt_von: '', erstkontakt_bis: '' });
  const [vorschau, setVorschau] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  /* von Hand (07.10.2026): jederzeit, unabhängig vom Regel-Filter — Leads anhaken und/oder eintragen */
  const [modus, setModus] = useState('regeln');
  const [gewaehlt, setGewaehlt] = useState([]);
  const [neue, setNeue] = useState([]);
  /* Für wen die Kampagne ist (07.10.2026, „Reaktivierung für Cagla"): Leitung sieht die Benutzerliste, sonst bleibt es bei der eigenen Person */
  const [nutzer, setNutzer] = useState([]);
  const [ownerId, setOwnerId] = useState('');
  const [telefonJeLead, setTelefonJeLead] = useState({});   // Nummern aus einer Liste für Leads ohne brauchbare Nummer
  const { user: ich } = useAuth();
  // Der Dialog bleibt gemountet: beim Öffnen frisch anfangen, sonst stehen alte Haken und Hand-Einträge wieder da
  useEffect(() => { if (offen) { setModus('regeln'); setGewaehlt([]); setNeue([]); setOwnerId(''); setTelefonJeLead({}); } }, [offen]);
  // nur Vertrieb und Leitung, nicht die eigene Person (die ist der Standard) und keine Stationskonten
  useEffect(() => { if (offen) authAPI.users().then(r => setNutzer((r.data || []).filter(u => u.name && u.user_id !== ich?.user_id && /vertrieb|admin|manager|leitung/i.test(`${u.role} ${u.role_name || ''}`)))).catch(() => setNutzer([])); }, [offen, ich?.user_id]);
  const filter = useCallback(() => ({
    tage_seit_kontakt: f.tage_seit_kontakt === '' ? 30 : Math.max(0, Number(f.tage_seit_kontakt) || 0), einwilligung: f.einwilligung,
    zielgruppe: f.zielgruppe || null, herkunft: f.herkunft || null,
    erstkontakt_von: f.erstkontakt_von || null, erstkontakt_bis: f.erstkontakt_bis || null,
  }), [f]);

  useEffect(() => {
    if (!offen || modus !== 'regeln') return undefined;
    let aktiv = true;
    setLaedt(true);
    anrufkampagnenAPI.vorschau(filter()).then(r => { if (aktiv) setVorschau(r.data); })
      .catch(() => { if (aktiv) setVorschau(null); }).finally(() => { if (aktiv) setLaedt(false); });
    return () => { aktiv = false; };
  }, [offen, filter, modus]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const anlegen = async () => {
    if (!f.name.trim()) { toast.error('Bitte einen Namen vergeben'); return; }
    setSpeichert(true);
    try {
      const daten = modus === 'hand'
        ? { name: f.name.trim(), ziel: Number(f.ziel) || 50, modus: 'hand', lead_ids: gewaehlt.map(l => l.lead_id), neue_leads: neue, owner_user_id: ownerId || null, telefon_je_lead: Object.keys(telefonJeLead).length ? telefonJeLead : null }
        : { name: f.name.trim(), ziel: Number(f.ziel) || 50, filter: filter() };
      const r = await anrufkampagnenAPI.anlegen(daten);
      const a = r.data.ausgeschlossen || {};
      const zusatz = modus === 'hand' ? [a.angelegt ? `${a.angelegt} neu angelegt` : '', a.gesperrt ? `${a.gesperrt} gesperrt übersprungen` : '', a.ohne_telefon ? `${a.ohne_telefon} ohne Nummer übersprungen` : '', a.telefon_ungueltig ? `${a.telefon_ungueltig} mit falsch gemeldeter Nummer übersprungen` : '', a.nicht_gefunden ? `${a.nicht_gefunden} nicht gefunden` : ''].filter(Boolean).join(' · ') : '';
      toast.success(`Kampagne angelegt: ${r.data.eintraege_gesamt} Leads in der Liste${zusatz ? ` (${zusatz})` : ''}`, { duration: 7000 });
      (r.data.hinweise || []).forEach(h => toast.warning(h, { duration: 10000 }));
      onFertig(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kampagne konnte nicht angelegt werden');
    }
    setSpeichert(false);
  };
  const z = vorschau?.zaehler;
  const bereit = modus === 'hand' ? gewaehlt.length + neue.length > 0 : !!z?.ausgewaehlt;

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Neue Anrufkampagne</DialogTitle></DialogHeader>
        <div className="flex gap-1.5" data-testid="ak-modus">
          <Button type="button" size="sm" variant={modus === 'regeln' ? 'default' : 'outline'} onClick={() => setModus('regeln')}>Nach Regeln</Button>
          <Button type="button" size="sm" variant={modus === 'hand' ? 'default' : 'outline'} onClick={() => setModus('hand')} data-testid="ak-modus-hand">Selbst zusammenstellen</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label className="text-xs">Name</Label><Input value={f.name} onChange={e => set('name', e.target.value)} placeholder="z. B. Herbstaktion Kickboxen" data-testid="ak-name" /></div>
          <div><Label className="text-xs">Ziel (Anrufe)</Label><Input type="number" min="1" value={f.ziel} onChange={e => set('ziel', e.target.value)} /></div>
          {modus === 'hand' && nutzer.length > 0 && (
            <div>
              <Label className="text-xs">Kampagne für</Label>
              <select value={ownerId} onChange={e => setOwnerId(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="ak-owner">
                <option value="">mich selbst</option>
                {nutzer.map(u => <option key={u.user_id} value={u.user_id}>{u.name}{u.role_name ? ` (${u.role_name})` : ''}</option>)}
              </select>
            </div>
          )}
          {modus === 'hand' && <div className="md:col-span-2 text-[11px] text-muted-foreground">Jederzeit möglich: Tage seit Kontakt, Herkunft und Zielgruppe spielen keine Rolle. Gesperrte Leads und Leads ohne Nummer bleiben draußen.</div>}
        </div>
        {/* bleibt gemountet (nur ausgeblendet), damit Suche, Papierliste und ein offener Abgleich den Moduswechsel überleben */}
        <div className={modus === 'hand' ? '' : 'hidden'}><LeadAuswahl gewaehlt={gewaehlt} setGewaehlt={setGewaehlt} neue={neue} setNeue={setNeue} setTelefonJeLead={setTelefonJeLead} /></div>
        {modus === 'hand' ? null : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Letzter Kontakt älter als (Tage)</Label><Input type="number" min="0" value={f.tage_seit_kontakt} onChange={e => set('tage_seit_kontakt', e.target.value)} /></div>
          <div>
            <Label className="text-xs">Einwilligung Telefon</Label>
            <select value={f.einwilligung} onChange={e => set('einwilligung', e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="ak-einwilligung">
              <option value="streng">Nur dokumentierte Einwilligung</option>
              <option value="anfrage">Auch Leads, die selbst angefragt haben</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Zielgruppe</Label>
            <select value={f.zielgruppe} onChange={e => set('zielgruppe', e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="">Kinder + Erwachsene</option><option value="erwachsene">Erwachsene</option><option value="kinder">Kinder</option>
            </select>
          </div>
          <div><Label className="text-xs">Herkunft (Kampagne/Quelle/Kanal, optional)</Label><Input value={f.herkunft} onChange={e => set('herkunft', e.target.value)} placeholder="z. B. instagram" /></div>
          <div className="flex gap-2">
            <div className="flex-1"><Label className="text-xs">Erstkontakt von</Label><Input type="date" value={f.erstkontakt_von} onChange={e => set('erstkontakt_von', e.target.value)} /></div>
            <div className="flex-1"><Label className="text-xs">bis</Label><Input type="date" value={f.erstkontakt_bis} onChange={e => set('erstkontakt_bis', e.target.value)} /></div>
          </div>
        </div>
        )}

        {modus === 'regeln' && <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs space-y-1" data-testid="ak-vorschau">
          {laedt && !z ? <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Vorschau wird berechnet…</span> : z ? (
            <>
              <div className="text-sm font-bold">{z.ausgewaehlt} Leads kommen in die Liste</div>
              <div className="text-muted-foreground">
                geprüft {z.geprueft} · ohne Einwilligung <b>{z.ohne_einwilligung}</b> · aktiver Vertrag {z.aktiver_vertrag} · gesperrt {z.gesperrt} · ohne Telefon {z.ohne_telefon} · zu frisch {z.zu_frisch} · Filter {z.filter}
              </div>
              {z.ausgewaehlt === 0 && (
                <div className="flex items-start gap-1 text-amber-700" data-testid="ak-leer">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-none" />
                  <span>Niemand übrig. {z.zu_frisch > 0 && 'Mit „Letzter Kontakt älter als“ kleiner wird die Liste länger. '}{z.ohne_einwilligung > 0 && f.einwilligung === 'streng' && 'Oder „Auch Leads, die selbst angefragt haben“ wählen.'}</span>
                </div>
              )}
              {z.ohne_einwilligung > 0 && f.einwilligung === 'streng' && (
                <div className="flex items-start gap-1 text-amber-700"><Info className="w-3.5 h-3.5 mt-0.5 flex-none" /> Ohne dokumentierte Telefon-Einwilligung darf nicht angerufen werden (UWG § 7). Ob „selbst angefragt“ genügt, ist Philips Entscheidung.</div>
              )}
              {vorschau.beispiele?.length > 0 && <div className="text-muted-foreground">z. B. {vorschau.beispiele.slice(0, 5).map(b => b.name).join(', ')}</div>}
            </>
          ) : <span className="text-muted-foreground">Keine Vorschau</span>}
        </div>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={anlegen} disabled={speichert || !bereit} className="bg-marke hover:bg-marke-dunkel" data-testid="ak-anlegen">{speichert ? 'Legt an…' : 'Kampagne anlegen'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Slot-Picker (Schnellerfassungs-Kern: Kurs -> freie Termine) ───────── */
function SlotPicker({ zielgruppe, terminId, setTerminId }) {
  const [kurse, setKurse] = useState([]);
  const [kurs, setKurs] = useState('');
  const [slots, setSlots] = useState(null);
  useEffect(() => { schnellerfassungAPI.kurse().then(r => setKurse(r.data || [])).catch(() => setKurse([])); }, []);
  useEffect(() => {
    if (!kurs) { setSlots(null); setTerminId(''); return; }
    let aktiv = true;
    schnellerfassungAPI.slots({ kurs_slug: kurs }).then(r => { if (!aktiv) return; const s = r.data || []; setSlots(s); setTerminId(s[0]?.termin_id || ''); })
      .catch(() => { if (aktiv) { setSlots([]); toast.error('Termine konnten nicht geladen werden'); } });
    return () => { aktiv = false; };
  }, [kurs, setTerminId]);
  const passend = kurse.filter(k => zielgruppe === 'kinder' ? /kid|mini|kinder/i.test(k.slug + k.name) : !/kid|mini|kinder/i.test(k.slug + k.name));
  return (
    <div className="space-y-2" data-testid="ak-slotpicker">
      <select value={kurs} onChange={e => setKurs(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
        <option value="">Kurs wählen…</option>
        {(passend.length ? passend : kurse).map(k => <option key={k.slug} value={k.slug}>{k.name}</option>)}
      </select>
      {slots && (slots.length === 0 ? <p className="text-xs text-muted-foreground">Keine freien Termine in den nächsten Tagen.</p> : (
        <div className="max-h-44 overflow-y-auto space-y-1">
          {slots.map(s => (
            <button key={s.termin_id} onClick={() => setTerminId(s.termin_id)}
              className={`w-full text-left px-2 py-1.5 rounded-md border text-xs ${terminId === s.termin_id ? 'border-marke bg-marke/5' : 'border-border hover:bg-muted'}`}>
              {s.label} <span className="text-muted-foreground">· {s.restplaetze} frei</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─── Gesprächsmaske ────────────────────────────────────────────────────── */
function Gespraech({ kampagne, eintragId, skript, werte, onClose, onFertig }) {
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [ergebnis, setErgebnis] = useState('');
  const [rueckruf, setRueckruf] = useState('');
  const [grund, setGrund] = useState('');
  const [grundText, setGrundText] = useState('');
  const [notiz, setNotiz] = useState('');
  const [terminId, setTerminId] = useState('');
  const [sendet, setSendet] = useState(false);
  const [zweites, setZweites] = useState(null);          // Teil C: 409 zweites_probetraining
  const [begruendung, setBegruendung] = useState('');

  const laden = useCallback(async (eid) => {
    setLaedt(true);
    try {
      const r = await anrufkampagnenAPI.naechster(kampagne.id, eid);
      setDaten(r.data?.eintrag ? r.data : null);
    } catch { setDaten(null); }
    setErgebnis(''); setRueckruf(''); setGrund(''); setGrundText(''); setNotiz(''); setTerminId('');
    setZweites(null); setBegruendung('');
    setLaedt(false);
  }, [kampagne.id]);
  useEffect(() => { laden(eintragId); }, [laden, eintragId]);

  const speichern = async (weiter) => {
    if (!ergebnis) { toast.error('Bitte ein Ergebnis wählen — das ist Pflicht'); return; }
    if (ergebnis === 'rueckruf_vereinbart' && !rueckruf) { toast.error('Bei „Rückruf vereinbart“ ist das Datum Pflicht'); return; }
    if (ergebnis === 'kein_interesse' && !grund) { toast.error('Bei „kein Interesse“ ist der Grund Pflicht'); return; }
    if (ergebnis === 'kein_interesse' && grund === 'sonstiges' && !grundText.trim()) { toast.error('Bitte den Grund in Worten angeben'); return; }
    if (ergebnis === 'probetraining_gebucht' && !terminId) { toast.error('Bitte einen Termin wählen'); return; }
    if (zweites && !begruendung.trim()) { toast.error('Bitte eine Begründung für das zweite Probetraining angeben'); return; }
    setSendet(true);
    try {
      const r = await anrufkampagnenAPI.ergebnis(kampagne.id, daten.eintrag.id, {
        ergebnis, rueckruf_am: rueckruf ? new Date(rueckruf).toISOString() : null,
        verlustgrund: grund || null, verlustgrund_text: grundText, notiz, termin_id: terminId || null,
        begruendung_zweites: zweites ? begruendung.trim() : null,
      });
      toast.success(r.data.hinweis || 'Gespeichert');
      onFertig();
      if (weiter) laden(undefined); else onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'zweites_probetraining') { setZweites(d); toast.warning('Schon ein Probetraining wahrgenommen — Begründung nötig'); }
      else toast.error((typeof d === 'string' && d) || 'Konnte nicht gespeichert werden');
    }
    setSendet(false);
  };

  const e = daten?.eintrag; const lead = daten?.lead || {};
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PhoneCall className="w-4 h-4 text-marke" /> Gespräch — {kampagne.name}</DialogTitle></DialogHeader>
        {laedt ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          : !e ? (
            <div className="py-10 text-center space-y-2">
              <p className="text-sm font-medium">Kein fälliger Lead mehr in dieser Kampagne.</p>
              <p className="text-xs text-muted-foreground">Rückrufe und Wiederholungsversuche erscheinen, sobald sie fällig sind.</p>
              <Button variant="outline" onClick={onClose}>Schließen</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr_1fr] gap-4" data-testid="ak-gespraech">
              {/* Links: Lead */}
              <div className="space-y-3">
                <div className="rounded-lg border border-border/60 p-3">
                  <div className="text-lg font-extrabold">{e.name}</div>
                  <div className="text-sm flex items-center gap-2 mt-1"><Phone className="w-3.5 h-3.5 text-muted-foreground" /><a href={`tel:${e.telefon}`} className="font-mono text-marke font-bold">{e.telefon}</a></div>
                  {e.email && <div className="text-xs flex items-center gap-2 mt-1 text-muted-foreground"><Mail className="w-3.5 h-3.5" />{e.email}</div>}
                  <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                    <div>Herkunft: <b>{e.herkunft}</b> · {e.zielgruppe === 'kinder' ? 'Kind/Jugend' : 'Erwachsene'}</div>
                    <div>Erstkontakt: {datum(lead.created_at)} · letzter Kontakt: {datum(e.letzter_kontakt)}</div>
                    <div>Stufe: {lead.stage || '—'} · Einwilligung: {EINWILLIGUNG_LABEL[e.einwilligung] || e.einwilligung}</div>
                    {e.notiz && <div className="mt-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-amber-800" data-testid="ak-gespraech-notiz"><b>Notiz:</b> {e.notiz}</div>}
                    {e.einwilligung === 'von_hand' && <div className="mt-1 flex items-start gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-amber-800"><Info className="w-3.5 h-3.5 mt-0.5 flex-none" /> Keine dokumentierte Telefon-Einwilligung — von Hand in die Liste gelegt (UWG § 7).</div>}
                    {e.mitglied && <div className="mt-1 flex items-start gap-1 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1 text-emerald-800"><Info className="w-3.5 h-3.5 mt-0.5 flex-none" /> Mitglied mit Vertrag: Die Akte bleibt unangetastet, kein Probetraining; „nicht mehr kontaktieren“ sperrt nur Werbeanrufe.</div>}
                    <div>Versuche: {e.versuche} von {werte?.max_versuche ?? 3}</div>
                  </div>
                  <Button variant="link" size="sm" className="px-0 h-6 text-xs" onClick={() => window.open(`/leads/${e.lead_id}`, '_blank')}>Lead öffnen <ChevronRight className="w-3 h-3" /></Button>
                </div>
                {(lead.verloren_grund || daten.frueheres_gespraech || daten.probetrainings?.some(p => p.verlustgrund)) && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs space-y-1">
                    <div className="font-bold">Frühere Verlustgründe</div>
                    {lead.verloren_grund && <div>Lead: {lead.verloren_grund}</div>}
                    {daten.frueheres_gespraech && <div>Gespräch {datum(daten.frueheres_gespraech.zeit)}: {daten.frueheres_gespraech.ergebnis}{daten.frueheres_gespraech.grund ? ` — ${daten.frueheres_gespraech.grund}` : ''}</div>}
                    {daten.probetrainings?.filter(p => p.verlustgrund).map(p => <div key={p._id}>Probetraining {datum(p.start)}: {p.verlustgrund}{p.verlustgrund_text ? ` (${p.verlustgrund_text})` : ''}</div>)}
                  </div>
                )}
                {daten.probetrainings?.length > 0 && (
                  <div className="rounded-lg border border-border/60 p-3 text-xs space-y-1">
                    <div className="font-bold">Probetrainings</div>
                    {daten.probetrainings.map(p => <div key={p._id}>{datumZeit(p.start)} · {p.status}{p.ergebnis ? ` · ${p.ergebnis}` : ''}</div>)}
                  </div>
                )}
                {daten.aktivitaeten?.length > 0 && (
                  <div className="rounded-lg border border-border/60 p-3 text-xs space-y-1 max-h-48 overflow-y-auto">
                    <div className="font-bold">Zeitstrahl</div>
                    {daten.aktivitaeten.map((a, i) => <div key={i} className="text-muted-foreground"><span className="text-foreground">{datum(a.created_at)}</span> · {a.type}: {(a.notes || '').slice(0, 120)}</div>)}
                  </div>
                )}
                {lead.notes && <div className="rounded-lg border border-border/60 p-3 text-xs"><div className="font-bold">Notizen</div><div className="whitespace-pre-wrap text-muted-foreground">{String(lead.notes).slice(0, 600)}</div></div>}
              </div>

              {/* Mitte: Skript */}
              <div className="rounded-lg border border-border/60 p-4 bg-card max-h-[70vh] overflow-y-auto" data-testid="ak-skript">
                <Markdown text={skript} />
              </div>

              {/* Rechts: Ergebnis */}
              <div className="space-y-3">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ergebnis (Pflicht)</div>
                <div className="grid grid-cols-1 gap-1.5">
                  {ERGEBNIS.map(o => (
                    <button key={o.key} onClick={() => setErgebnis(o.key)} data-testid={`ak-ergebnis-${o.key}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left ${ergebnis === o.key ? 'border-marke bg-marke/5 font-bold' : `${o.cls} hover:bg-muted`}`}>
                      <o.icon className="w-4 h-4" /> {o.label}
                    </button>
                  ))}
                </div>
                {ergebnis === 'rueckruf_vereinbart' && (
                  <div><Label className="text-xs">Rückruf am (Pflicht)</Label><Input type="datetime-local" value={rueckruf} onChange={e2 => setRueckruf(e2.target.value)} data-testid="ak-rueckruf" /></div>
                )}
                {ergebnis === 'kein_interesse' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Grund (Pflicht)</Label>
                    <select value={grund} onChange={e2 => setGrund(e2.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="ak-grund">
                      <option value="">— bitte wählen —</option>
                      {(werte?.verlustgruende || []).map(g => <option key={g.grund} value={g.grund}>{g.label}</option>)}
                    </select>
                    {grund === 'sonstiges' && <Input value={grundText} onChange={e2 => setGrundText(e2.target.value)} placeholder="Grund in Worten" />}
                  </div>
                )}
                {ergebnis === 'probetraining_gebucht' && (
                  <div><Label className="text-xs">Termin (Pflicht)</Label><SlotPicker zielgruppe={e.zielgruppe} terminId={terminId} setTerminId={setTerminId} /></div>
                )}
                {zweites && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2 text-xs">
                    <p className="font-bold text-amber-800 dark:text-amber-300">⚠ {zweites.nachricht}</p>
                    <Input value={begruendung} onChange={e2 => setBegruendung(e2.target.value)} placeholder="Begründung (Pflicht)" />
                  </div>
                )}
                {ergebnis === 'nicht_mehr_kontaktieren' && (
                  <p className="text-xs text-red-600">Sperrt den Lead für alle Kampagnen und Automationen. Das lässt sich nicht per Klick zurücknehmen.</p>
                )}
                <div><Label className="text-xs">Notiz</Label><Input value={notiz} onChange={e2 => setNotiz(e2.target.value)} placeholder="kurz, was besprochen wurde" /></div>
                <div className="flex flex-col gap-1.5 pt-1">
                  <Button onClick={() => speichern(true)} disabled={sendet || !ergebnis} className="bg-marke hover:bg-marke-dunkel" data-testid="ak-speichern-weiter">{sendet ? 'Speichert…' : 'Speichern & nächster Lead'}</Button>
                  <Button variant="outline" onClick={() => speichern(false)} disabled={sendet || !ergebnis}>Speichern & schließen</Button>
                </div>
              </div>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Kampagne: Übersicht ───────────────────────────────────────────────── */
function KampagneDetail({ id, werte, skript }) {
  const navigate = useNavigate();
  const [k, setK] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [gespraech, setGespraech] = useState(null);      // { eintragId } | null
  /* Leads nachlegen (07.10.2026): jederzeit in die laufende Kampagne, angehakt oder von Hand */
  const [nachlegen, setNachlegen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState([]);
  const [neue, setNeue] = useState([]);
  const [telefonJeLead, setTelefonJeLead] = useState({});
  const [legtNach, setLegtNach] = useState(false);
  const laden = useCallback(async () => {
    try { const r = await anrufkampagnenAPI.uebersicht(id); setK(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Kampagne konnte nicht geladen werden'); }
    setLaedt(false);
  }, [id]);
  useEffect(() => { laden(); }, [laden]);

  const oeffnen = async () => {
    try { await anrufkampagnenAPI.oeffnen(id); toast.success('Kampagne ist wieder bearbeitbar'); laden(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht geöffnet werden'); }
  };
  const beenden = async () => {
    try { await anrufkampagnenAPI.beenden(id); toast.success('Kampagne beendet'); laden(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht beenden'); }
  };
  const nachlegenSchliessen = () => { setNachlegen(false); setGewaehlt([]); setNeue([]); setTelefonJeLead({}); };
  /* Eintrag aus der Liste nehmen (07.10.2026): nur ohne Anruf, zwei Klicks statt Browser-Nachfrage */
  const [entfernenFrage, setEntfernenFrage] = useState(null);
  const entfernen = async (e) => {
    if (entfernenFrage !== e.id) { setEntfernenFrage(e.id); return; }
    try {
      const r = await anrufkampagnenAPI.entfernen(id, e.id);
      toast.success(`${r.data.entfernt} aus der Liste genommen`);
      setEntfernenFrage(null); laden();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Konnte nicht entfernen'); setEntfernenFrage(null); }
  };
  /* Notiz am Eintrag (07.10.2026): ohne Anruf-Ergebnis, steht in der Liste und am Lead */
  const [notizEdit, setNotizEdit] = useState(null);   // { id, text } | null
  const notizSpeichern = async () => {
    if (!notizEdit) return;
    try {
      await anrufkampagnenAPI.notiz(id, notizEdit.id, notizEdit.text.trim());
      toast.success(notizEdit.text.trim() ? 'Notiz gespeichert' : 'Notiz gelöscht');
      setNotizEdit(null); laden();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Notiz konnte nicht gespeichert werden'); }
  };
  const nachlegenSpeichern = async () => {
    setLegtNach(true);
    try {
      const r = await anrufkampagnenAPI.hinzufuegen(id, { lead_ids: gewaehlt.map(l => l.lead_id), neue_leads: neue, telefon_je_lead: Object.keys(telefonJeLead).length ? telefonJeLead : null });
      const z = r.data.zaehler || {};
      const gruende = [r.data.schon_drin ? `${r.data.schon_drin} waren schon drin` : '', r.data.nicht_mehr_offen ? `${r.data.nicht_mehr_offen} in dieser Kampagne schon erledigt oder ausgefallen` : '',
        z.gesperrt ? `${z.gesperrt} gesperrt` : '', z.ohne_telefon ? `${z.ohne_telefon} ohne Nummer` : '', z.telefon_ungueltig ? `${z.telefon_ungueltig} mit falsch gemeldeter Nummer` : '',
        z.nicht_gefunden ? `${z.nicht_gefunden} nicht gefunden` : '', z.mehrdeutig ? `${z.mehrdeutig} mit mehrdeutiger Nummer` : '', z.unbrauchbar ? `${z.unbrauchbar} unvollständig` : '', z.angelegt ? `${z.angelegt} neu angelegt` : ''].filter(Boolean).join(', ');
      (r.data.hinweise || []).forEach(h => toast.warning(h, { duration: 10000 }));
      if (r.data.hinzugefuegt > 0) {
        toast.success(`${r.data.hinzugefuegt} Leads dazu${gruende ? ` (${gruende})` : ''}`, { duration: 7000 });
        nachlegenSchliessen(); laden();
      } else {
        toast.warning(`Niemand dazugekommen${gruende ? `: ${gruende}` : ''}`, { duration: 8000 });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht nachlegen'); }
    setLegtNach(false);
  };

  if (laedt) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!k) return null;
  const f = k.fortschritt;
  const faellig = k.offen.filter(e => e.faellig).length;
  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="ak-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/kampagnen/anrufe')}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-extrabold flex items-center gap-2"><PhoneCall className="w-6 h-6 text-marke" /> {k.name}</h1>
            <p className="text-sm text-muted-foreground">Seit {datum(k.start)} · {k.owner?.name} · {k.status === 'aktiv' ? 'aktiv' : `beendet ${datum(k.ende)}`}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={laden}><RefreshCw className="w-4 h-4" /></Button>
          {k.status === 'aktiv' && <Button variant="outline" size="sm" onClick={() => setNachlegen(true)} data-testid="ak-nachlegen"><Plus className="w-4 h-4 mr-1" /> Leads dazu</Button>}
          {k.status === 'aktiv' && <Button variant="outline" size="sm" onClick={beenden}><StopCircle className="w-4 h-4 mr-1" /> Beenden</Button>}
          {k.status === 'beendet' && <Button variant="outline" size="sm" onClick={oeffnen} data-testid="kampagne-oeffnen">Wieder öffnen</Button>}
          {k.status === 'aktiv' && <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={!faellig} onClick={() => setGespraech({ eintragId: undefined })} data-testid="ak-naechster">
            <PhoneCall className="w-4 h-4 mr-1" /> Nächsten anrufen {faellig ? `(${faellig} fällig)` : ''}
          </Button>}
        </div>
      </div>

      {/* Fortschritt */}
      <Card><CardContent className="p-4 space-y-2">
        <div className="flex items-end justify-between">
          <div><span className="text-3xl font-extrabold tabular-nums">{f.anrufe}</span> <span className="text-muted-foreground text-sm">von {f.ziel} Anrufen</span></div>
          <span className={`text-sm font-bold ${f.anrufe >= f.ziel ? 'text-emerald-600' : 'text-muted-foreground'}`}>{f.prozent} %</span>
        </div>
        <div className="h-3 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${f.anrufe >= f.ziel ? 'bg-emerald-500' : 'bg-marke'}`} style={{ width: `${f.prozent}%` }} /></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 text-center">
          {[[UserCheck, 'Erreicht', f.erreicht], [CalendarClock, 'Probetrainings gebucht', f.gebucht], [Handshake, 'daraus Abschlüsse', f.abschluesse], [PhoneMissed, 'ausgefallen (3× nicht erreicht)', f.ausgefallen]].map(([Icon, l, w]) => (
            <div key={l}><div className="text-xl font-bold tabular-nums flex items-center justify-center gap-1"><Icon className="w-4 h-4 text-muted-foreground" />{w}</div><div className="text-[10px] text-muted-foreground">{l}</div></div>
          ))}
        </div>
        <div className="text-[10px] text-muted-foreground">
          Liste: {f.eintraege} Leads · offen {f.offen} · erledigt {f.erledigt} · gesperrt {f.gesperrt}.
          {k.modus === 'hand' ? ' Von Hand zusammengestellt' : k.modus === 'gemischt' ? ' Nach Regeln angelegt, von Hand ergänzt' : ' Nach Regeln angelegt'}
          {k.modus !== 'hand' && <> · ausgeschlossen beim Anlegen: ohne Einwilligung {k.ausgeschlossen?.ohne_einwilligung ?? 0}, aktiver Vertrag {k.ausgeschlossen?.aktiver_vertrag ?? 0}, gesperrt {k.ausgeschlossen?.gesperrt ?? 0}, zu frisch {k.ausgeschlossen?.zu_frisch ?? 0}</>}
          {k.kennzeichen && (k.kennzeichen.von_hand > 0 || k.kennzeichen.ohne_einwilligung > 0 || k.kennzeichen.mitglied > 0) && (
            <> · in der Liste: {k.kennzeichen.von_hand} von Hand, <b className={k.kennzeichen.ohne_einwilligung ? 'text-amber-700' : ''}>{k.kennzeichen.ohne_einwilligung} ohne dokumentierte Einwilligung</b>, {k.kennzeichen.mitglied} Mitglieder mit Vertrag, {k.kennzeichen.altbestand} Altbestand</>
          )}
          {(k.hinweise || []).length > 0 && <span className="block text-amber-700 mt-1">{k.hinweise.slice(-5).join(' · ')}</span>}
        </div>
      </CardContent></Card>

      {/* Offene Leads */}
      <Card><CardContent className="p-0">
        <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
          <h2 className="text-sm font-bold">Offene Leads</h2>
          <span className="text-xs text-muted-foreground">sortiert nach nächstem fälligen Versuch</span>
        </div>
        {k.offen.length === 0 ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">Alles abtelefoniert. 👊</div>
          : k.offen.map(e => (
            <div key={e.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-border/50 last:border-b-0" data-testid={`ak-offen-${e.id}`}>
              <div className="min-w-[200px] flex-1">
                <div className="text-sm font-bold flex flex-wrap items-center gap-1">{e.name} {e.rueckruf_am && <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Rückruf</span>}
                  {e.einwilligung === 'von_hand' && <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">ohne Einwilligung</span>}
                  {e.mitglied && <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">Mitglied</span>}
                  {!e.mitglied && STAND_KURZ[e.stand] && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${STAND_FARBE[e.stand]}`}>{STAND_KURZ[e.stand]}{e.stand === 'da_gewesen' ? ` (${e.checkins || 0} Check-ins, ${e.probetrainings_erschienen || 0} Probetr.)` : ''}</span>}
                  {e.eltern && <span className="text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Elternkonto</span>}
                  {e.altbestand && <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">Altbestand</span>}
                </div>
                <div className="text-xs text-muted-foreground">{e.herkunft} · {e.zielgruppe === 'kinder' ? 'Kind' : 'Erw.'} · letzter Kontakt {datum(e.letzter_kontakt)} · Versuche {e.versuche}</div>
              </div>
              <div className={`text-xs tabular-nums ${e.faellig ? 'text-emerald-600 font-bold' : 'text-muted-foreground'}`}>{e.faellig ? 'fällig' : `ab ${datumZeit(e.naechster_versuch)}`}</div>
              <a href={`tel:${e.telefon}`} className="text-xs font-mono text-muted-foreground">{e.telefon}</a>
              {k.status === 'aktiv' && <Button size="sm" variant="outline" className="h-7" onClick={() => setGespraech({ eintragId: e.id })}><PhoneCall className="w-3.5 h-3.5 mr-1" /> Anrufen</Button>}
              <button type="button" onClick={() => setNotizEdit(notizEdit?.id === e.id ? null : { id: e.id, text: e.notiz || '' })} className={`h-7 px-2 rounded-md text-xs border ${e.notiz ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-border/60 text-muted-foreground'} hover:bg-muted`} title="Notiz" data-testid={`ak-notiz-${e.id}`}>Notiz</button>
              {k.status === 'aktiv' && !(e.versuche > 0) && (
                <button type="button" onClick={() => entfernen(e)} onBlur={() => { if (entfernenFrage === e.id) setEntfernenFrage(null); }} className={`h-7 px-2 rounded-md text-xs border ${entfernenFrage === e.id ? 'border-red-300 bg-red-50 text-red-700' : 'border-border/60 text-muted-foreground hover:bg-muted'}`} title="Aus der Liste nehmen" data-testid={`ak-entfernen-${e.id}`}>
                  {entfernenFrage === e.id ? 'Wirklich raus?' : '×'}
                </button>
              )}
              {e.notiz && notizEdit?.id !== e.id && <div className="w-full text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">{e.notiz}<span className="text-amber-700/70"> · {e.notiz_von || ''} {datum(e.notiz_am)}</span></div>}
              {notizEdit?.id === e.id && (
                <div className="w-full flex flex-wrap gap-2 items-start">
                  <textarea value={notizEdit.text} onChange={ev => setNotizEdit({ id: e.id, text: ev.target.value })} rows={2} maxLength={1000} aria-label="Notiz zum Eintrag" placeholder="z. B. kennt Cagla vom Fest, lieber abends anrufen" className="flex-1 min-w-[220px] rounded-md border border-input bg-background px-2 py-1 text-xs" autoFocus />
                  <Button size="sm" className="h-7 bg-marke hover:bg-marke-dunkel" onClick={notizSpeichern} data-testid={`ak-notiz-speichern-${e.id}`}>Speichern</Button>
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => setNotizEdit(null)}>Abbrechen</Button>
                </div>
              )}
            </div>
          ))}
      </CardContent></Card>

      {k.erledigt.length > 0 && (
        <Card><CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/60"><h2 className="text-sm font-bold">Erledigt</h2></div>
          {k.erledigt.slice(0, 100).map(e => (
            <div key={e.id} className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-border/50 last:border-b-0 text-xs">
              <span className="font-medium flex-1 min-w-[160px]">{e.name}</span>
              <span className="text-muted-foreground">{k.ergebnis_label?.[e.ergebnis] || e.ergebnis || e.status}{e.verlustgrund ? ` · ${e.verlustgrund}` : ''}</span>
              <span className="text-muted-foreground">{datum(e.letzter_anruf)}</span>
            </div>
          ))}
        </CardContent></Card>
      )}

      {gespraech && <Gespraech kampagne={k} eintragId={gespraech.eintragId} skript={skript} werte={werte} onClose={() => setGespraech(null)} onFertig={laden} />}

      <Dialog open={nachlegen} onOpenChange={(o) => { if (!o) nachlegenSchliessen(); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Leads in „{k.name}“ nachlegen</DialogTitle></DialogHeader>
          <LeadAuswahl gewaehlt={gewaehlt} setGewaehlt={setGewaehlt} neue={neue} setNeue={setNeue} kampagneName={k.name} setTelefonJeLead={setTelefonJeLead} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={nachlegenSchliessen}>Abbrechen</Button>
            <Button onClick={nachlegenSpeichern} disabled={legtNach || gewaehlt.length + neue.length === 0} className="bg-marke hover:bg-marke-dunkel" data-testid="ak-nachlegen-speichern">{legtNach ? 'Legt nach…' : `${gewaehlt.length + neue.length} Leads dazu`}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Liste aller Kampagnen ─────────────────────────────────────────────── */
function KampagnenListe({ werte }) {
  const navigate = useNavigate();
  const [liste, setListe] = useState(null);
  const [neu, setNeu] = useState(false);
  const laden = useCallback(async () => {
    try { const r = await anrufkampagnenAPI.liste(); setListe(r.data.kampagnen || []); }
    catch { toast.error('Kampagnen konnten nicht geladen werden'); setListe([]); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="ak-liste">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><PhoneCall className="w-6 h-6 text-marke" /> Lead-Anrufliste</h1>
          <p className="text-sm text-muted-foreground">Kalte Leads strukturiert mit Skript abtelefonieren. Ziel je Kampagne: mindestens {werte?.standard_ziel ?? 50} Anrufe.</p>
        </div>
        <Button onClick={() => setNeu(true)} className="bg-marke hover:bg-marke-dunkel" data-testid="ak-neu"><Plus className="w-4 h-4 mr-1" /> Neue Kampagne</Button>
      </div>
      {liste == null ? <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        : liste.length === 0 ? (
          <Card><CardContent className="py-14 text-center">
            <PhoneCall className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Noch keine Kampagne. Leg die erste an — der Filter zeigt vorher, wie viele Leads anrufbar sind.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {liste.map(k => {
              const f = k.fortschritt;
              return (
                <div key={k.id} role="button" tabIndex={0} onClick={() => navigate(`/kampagnen/anrufe/${k.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/kampagnen/anrufe/${k.id}`); }}
                  className="rounded-xl border border-border/60 bg-card hover:bg-muted/40 px-4 py-3 cursor-pointer" data-testid={`ak-kampagne-${k.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-sm font-bold flex items-center gap-2">{k.name} <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${k.status === 'aktiv' ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>{k.status}</span>
                        {k.modus === 'hand' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">von Hand</span>}
                        {k.modus === 'gemischt' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">Regeln + von Hand</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">seit {datum(k.start)} · {k.owner?.name} · {f.eintraege} Leads · offen {f.offen}</div>
                    </div>
                    <div className="text-xs text-muted-foreground text-right">erreicht {f.erreicht} · gebucht {f.gebucht} · Abschlüsse {f.abschluesse}</div>
                    <div className="w-40">
                      <div className="flex justify-between text-[10px] text-muted-foreground"><span>{f.anrufe} / {f.ziel}</span><span>{f.prozent} %</span></div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${f.anrufe >= f.ziel ? 'bg-emerald-500' : 'bg-marke'}`} style={{ width: `${f.prozent}%` }} /></div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <NeueKampagne offen={neu} onClose={() => setNeu(false)} onFertig={(k) => { setNeu(false); navigate(`/kampagnen/anrufe/${k.id}`); }} werte={werte} />
    </div>
  );
}

export default function AnrufkampagnenPage() {
  const { id } = useParams();
  const [werte, setWerte] = useState(null);
  const [skript, setSkript] = useState('');
  useEffect(() => {
    anrufkampagnenAPI.werte().then(r => setWerte(r.data)).catch(() => {});
    anrufkampagnenAPI.skript().then(r => setSkript(r.data.markdown || '')).catch(() => setSkript('# Skript konnte nicht geladen werden'));
  }, []);
  return id ? <KampagneDetail id={id} werte={werte} skript={skript} /> : <KampagnenListe werte={werte} />;
}
